import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { entityService } from './entity.service';
import { setAlertError, setAlertSuccess } from '../alert/alert.slice';

const initialState = {
  entity: {},
  adminUsers: [], // Add a new array to hold the admin users
  loading: false,
  adding: false,
  updating: false,
  deleting: false,
  fetchingAdminUsers: false // For tracking loading state for admin users
};

export const getEntityByID = createAsyncThunk('entity/getEntityByID', async (id, thunkAPI) => {
  try {
    return await entityService.getEntityByID(id);
  } catch (error) {
    const err = error.response?.data || { message: 'Failed to fetch entity.' };
    thunkAPI.dispatch(setAlertError(err.message));
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const getEntityAdminUsers = createAsyncThunk(
  'entity/getEntityAdminUsers',
  async (entityId, thunkAPI) => {
    try {
      return await entityService.getEntityAdminUsers(entityId);
    } catch (error) {
      const err = error.response?.data || { message: 'Failed to fetch admin users.' };
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const saveEntityAdminUser = createAsyncThunk(
  'entity/saveEntityAdminUser',
  async (data, thunkAPI) => {
    try {
      return await entityService.saveEntityAdminUser(data.entity, data);
    } catch (error) {
      const err = error.response?.data || { message: 'Failed to fetch admin users.' };
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const updateEntityAdminUser = createAsyncThunk(
  'entity/updateEntityAdminUser',
  async (data, thunkAPI) => {
    try {
      return await entityService.updateEntityAdminUser(data.id, data);
    } catch (error) {
      const err = error.response?.data || { message: 'Failed to update admin user.' };
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const deleteEntityAdminUser = createAsyncThunk(
  'entity/deleteEntityAdminUser',
  async (id, thunkAPI) => {
    try {
      return await entityService.deleteEntityAdminUser(id);
    } catch (error) {
      const err = error.response?.data || { message: 'Failed to remove Entity Admin User' };
      thunkAPI.dispatch(setAlertError(err.message));
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const addEntity = createAsyncThunk('entity/addEntity', async (data, thunkAPI) => {
  try {
    const response = await entityService.addEntity(data);
    thunkAPI.dispatch(setAlertSuccess('Entity added successfully'));
    return response;
  } catch (error) {
    const err = error.response?.data || { message: 'Failed to add entity.' };
    thunkAPI.dispatch(setAlertError(err.message));
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const updateEntity = createAsyncThunk('entity/updateEntity', async (data, thunkAPI) => {
  try {
    const response = await entityService.updateEntity(data);
    thunkAPI.dispatch(setAlertSuccess('Entity updated successfully'));
    return response;
  } catch (error) {
    const err = error.response?.data || { message: 'Failed to update entity.' };
    thunkAPI.dispatch(setAlertError(err.message));
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const deleteEntity = createAsyncThunk('entity/deleteEntity', async (id, thunkAPI) => {
  try {
    const response = await entityService.deleteEntity(id);
    thunkAPI.dispatch(setAlertSuccess('Entity deleted successfully'));
    return response;
  } catch (error) {
    const err = error.response?.data || { message: 'Failed to delete entity.' };
    thunkAPI.dispatch(setAlertError(err.message));
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const entitySlice = createSlice({
  name: 'entity',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEntityByID.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEntityByID.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.entity[0];
      })
      .addCase(getEntityByID.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getEntityAdminUsers.pending, (state) => {
        state.fetchingAdminUsers = true;
      })
      .addCase(getEntityAdminUsers.fulfilled, (state, action) => {
        state.fetchingAdminUsers = false;
        state.adminUsers = action.payload.users;
      })
      .addCase(getEntityAdminUsers.rejected, (state) => {
        state.fetchingAdminUsers = false;
      })
      .addCase(saveEntityAdminUser.pending, (state) => {
        state.fetchingAdminUsers = true;
      })
      .addCase(saveEntityAdminUser.fulfilled, (state, action) => {
        state.fetchingAdminUsers = false;
        state.adminUsers = action.payload.users;
      })
      .addCase(saveEntityAdminUser.rejected, (state) => {
        state.fetchingAdminUsers = false;
      })
      .addCase(addEntity.pending, (state) => {
        state.adding = true;
      })
      .addCase(addEntity.fulfilled, (state, action) => {
        state.adding = false;
        state.entity = action.payload.data;
      })
      .addCase(addEntity.rejected, (state) => {
        state.adding = false;
      })
      .addCase(updateEntity.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateEntity.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(updateEntity.rejected, (state) => {
        state.updating = false;
      })
      .addCase(deleteEntity.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteEntity.fulfilled, (state) => {
        state.deleting = false;
        state.entity = null;
      })
      .addCase(deleteEntity.rejected, (state) => {
        state.deleting = false;
      });
  }
});

export default entitySlice.reducer;
